<script lang="ts">
  import {
    PendingDeadlineSeconds,
    retryAiCreditAccountOpen,
    retryPendingPurchases,
    UiKeys,
  } from '@voicedojo/backend-types';
  import { api } from '$lib/api/client';
  import { liveReadModel } from '$lib/api/liveReadModel.svelte';
  import { t } from '$lib/i18n';
  import { auth, refresh } from '$lib/stores/auth';
  import PendingNotice from '$lib/ui/PendingNotice.svelte';

  const user = $derived($auth.status === 'authenticated' && !$auth.user.impersonation ? $auth.user : null);
  const creditsSince = $derived(user?.creditAccountPendingSince ?? null);
  const purchaseSince = $derived(user?.purchasePendingSince ?? null);

  // The open lands on the AiCreditAccount read model, which the User push does not cover.
  $effect(() => {
    if (!creditsSince) return;
    const live = liveReadModel<null>({
      model: 'AiCreditAccount',
      initialValue: null,
      load: async () => {
        await refresh();
        return null;
      },
    });
    void live.start();
    return () => live.stop();
  });

  const refetch = (): void => void refresh();
</script>

{#if user && (creditsSince || purchaseSince)}
  <div class="account-setup no-print">
    {#if user && creditsSince}
      <PendingNotice
        since={creditsSince}
        deadlineSeconds={PendingDeadlineSeconds.CreditAccount}
        pendingText={t(UiKeys.Pending.CreditsSettingUp)}
        failedText={t(UiKeys.Pending.CreditsStuck)}
        onRetry={() => api.command(retryAiCreditAccountOpen, { id: user.id }, { requiresAuth: true })}
        onDeadline={refetch}
        data-testid="credits-pending"
      />
    {/if}
    {#if user && purchaseSince}
      <PendingNotice
        since={purchaseSince}
        deadlineSeconds={PendingDeadlineSeconds.PendingPurchase}
        pendingText={t(UiKeys.Pending.PurchaseApplying)}
        failedText={t(UiKeys.Pending.PurchaseStuck)}
        onRetry={() => api.command(retryPendingPurchases, { id: user.id }, { requiresAuth: true })}
        onDeadline={refetch}
        data-testid="purchase-pending"
      />
    {/if}
  </div>
{/if}

<style lang="scss">
  .account-setup {
    display: grid;
    gap: 0.5rem;
    margin-block: 0.75rem;
  }
</style>
